/**
 * ReviewForm — modal-friendly submission form for /reviews.
 *
 * Two-step flow behind one screen:
 *   1. POST /api/reviews with the text body → returns { id, status }.
 *   2. If photos are attached: POST /api/reviews/{id}/photos multipart.
 *
 * Honeypot: a visually-hidden `honeypot` input labelled `Website` — bots
 * that auto-fill every input trip it, humans leave it blank, server
 * silently swallows.
 *
 * Client-side guardrails (server re-validates):
 *   - reviewer_name 1-100 chars
 *   - star_rating 1-5, required
 *   - review_text 10-3000 chars, plaintext (we don't submit HTML)
 *   - preferred_contact 'email'|'phone'
 *   - contact_value validated per channel
 *   - photos: max 3 files, each ≤ 5MB, mime image/*
 *
 * data-testids: review-form, review-form-name, star-rating,
 *   review-form-service, review-form-text, review-form-contact-email,
 *   review-form-contact-phone, review-form-contact-value,
 *   review-photo-input, review-photo-thumb-{i}, review-photo-remove-{i},
 *   review-form-honeypot, review-form-submit, review-form-error,
 *   review-form-success.
 */

import { useState, useRef } from "react";
import { Upload, X } from "lucide-react";
import StarRating from "./StarRating";
import { reviewsApi } from "../../lib/reviewsApi";
import { SERVICES } from "../../data/content";

const MAX_PHOTOS = 3;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = /^image\/(jpe?g|png|webp|heic|heif)$/i;

function validEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
}

function validPhone(v) {
    const digits = (v || "").replace(/\D/g, "");
    return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
}

const INITIAL_STATE = {
    reviewer_name: "",
    star_rating: 0,
    review_text: "",
    preferred_contact: "email",
    contact_value: "",
    service_used: "",
    honeypot: "",
};

export default function ReviewForm({ onSuccess, renderSuccess }) {
    const [form, setForm] = useState(INITIAL_STATE);
    const [photos, setPhotos] = useState([]); // File[]
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onPickFiles = (fileList) => {
        setError("");
        const incoming = Array.from(fileList || []);
        if (photos.length + incoming.length > MAX_PHOTOS) {
            setError(`You may attach up to ${MAX_PHOTOS} photos.`);
            return;
        }
        const validated = [];
        for (const f of incoming) {
            if (!ALLOWED_MIME.test(f.type)) {
                setError(`Unsupported file type: ${f.name}`);
                return;
            }
            if (f.size > MAX_PHOTO_BYTES) {
                setError(`${f.name} exceeds the 5MB limit.`);
                return;
            }
            validated.push(f);
        }
        setPhotos((prev) => [...prev, ...validated]);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer?.files) onPickFiles(e.dataTransfer.files);
    };

    const removePhoto = (idx) => {
        setPhotos((prev) => prev.filter((_, i) => i !== idx));
    };

    const validate = () => {
        if (!form.reviewer_name.trim()) return "Please enter your name.";
        if (form.reviewer_name.length > 100) return "Name too long.";
        if (!form.star_rating || form.star_rating < 1 || form.star_rating > 5)
            return "Please choose a star rating.";
        const text = form.review_text.trim();
        if (text.length < 10) return "Review must be at least 10 characters.";
        if (text.length > 3000) return "Review may not exceed 3,000 characters.";
        if (form.preferred_contact === "email" && !validEmail(form.contact_value))
            return "Please enter a valid email address.";
        if (form.preferred_contact === "phone" && !validPhone(form.contact_value))
            return "Please enter a valid US phone number (10 digits).";
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        const problem = validate();
        if (problem) {
            setError(problem);
            return;
        }

        setSubmitting(true);
        try {
            const result = await reviewsApi.submitReview({
                reviewer_name: form.reviewer_name.trim(),
                star_rating: form.star_rating,
                review_text: form.review_text.trim(),
                preferred_contact: form.preferred_contact,
                contact_value: form.contact_value.trim(),
                service_used: form.service_used || null,
                honeypot: form.honeypot,
            });

            if (result?.id && photos.length > 0) {
                try {
                    await reviewsApi.uploadReviewPhotos(result.id, photos);
                } catch (photoErr) {
                    // Photo failure shouldn't destroy the review — surface
                    // as a soft note in the success state.
                    console.warn("Photo upload failed:", photoErr);
                }
            }
            setSuccess(true);
            onSuccess?.(result);
        } catch (err) {
            setError(err.message || "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const charsLeft = 3000 - form.review_text.length;
    const inputCls =
        "w-full bg-[#0F1013] border border-[rgba(201,168,106,0.2)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-[15px] py-2.5 px-3 transition-colors duration-300 placeholder:text-[#6e6a62]";
    const labelCls =
        "block text-[10.5px] tracking-[0.28em] uppercase text-[#9A958A] mb-2";

    if (success) {
        // Consumer can supply a custom success view (e.g. the dedicated
        // /leave-a-review page emits its own thank-you card with CTAs).
        // Falls back to the built-in acknowledgement used inside the
        // /reviews modal.
        if (typeof renderSuccess === "function") {
            return renderSuccess({
                reviewerName: form.reviewer_name,
                preferredContact: form.preferred_contact,
            });
        }
        return (
            <div
                className="text-center py-8"
                data-testid="review-form-success"
                role="status"
            >
                <div className="font-serif text-3xl md:text-4xl text-[#F3EFE7] mb-4 italic">
                    Thank you.
                </div>
                <p className="text-[#9A958A] text-sm md:text-base leading-[1.85] max-w-md mx-auto">
                    Your review has arrived with our concierge team. Once
                    reviewed, it will appear on our wall of client voices.
                    {form.preferred_contact === "email" &&
                        " Check your inbox for a note of thanks."}
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            data-testid="review-form"
            noValidate
            className="space-y-8"
        >
            {/* Name */}
            <div>
                <label className={labelCls} htmlFor="reviewer_name">
                    Your name
                </label>
                <input
                    id="reviewer_name"
                    type="text"
                    required
                    maxLength={100}
                    value={form.reviewer_name}
                    onChange={(e) => setField("reviewer_name", e.target.value)}
                    className={inputCls}
                    data-testid="review-form-name"
                    placeholder="How would you like to be identified?"
                />
            </div>

            {/* Star rating */}
            <div>
                <label className={labelCls}>Star rating</label>
                <StarRating
                    value={form.star_rating}
                    onChange={(n) => setField("star_rating", n)}
                    interactive
                    size={30}
                    data-testid="star-rating"
                    ariaLabel="Star rating (1 to 5)"
                />
            </div>

            {/* Service used */}
            <div>
                <label className={labelCls} htmlFor="service_used">
                    Service (optional)
                </label>
                <select
                    id="service_used"
                    value={form.service_used}
                    onChange={(e) => setField("service_used", e.target.value)}
                    className={inputCls}
                    data-testid="review-form-service"
                >
                    <option value="">General</option>
                    {SERVICES.map((s) => (
                        <option key={s.slug} value={s.slug}>
                            {s.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Review text */}
            <div>
                <label className={labelCls} htmlFor="review_text">
                    Your review
                </label>
                <textarea
                    id="review_text"
                    required
                    minLength={10}
                    maxLength={3000}
                    rows={7}
                    value={form.review_text}
                    onChange={(e) => setField("review_text", e.target.value)}
                    className={`${inputCls} resize-vertical`}
                    data-testid="review-form-text"
                    placeholder="Please share your experience with our team. Plaintext only — HTML tags are stripped."
                />
                <div className="mt-2 text-[11px] text-[#6e6a62]">
                    {charsLeft.toLocaleString()} characters remaining
                </div>
            </div>

            {/* Preferred contact */}
            <div>
                <span className={labelCls}>Preferred contact</span>
                <div className="flex items-center gap-6">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="preferred_contact"
                            value="email"
                            checked={form.preferred_contact === "email"}
                            onChange={() => {
                                setField("preferred_contact", "email");
                                setField("contact_value", "");
                            }}
                            data-testid="review-form-contact-email"
                            className="accent-[#C9A86A]"
                        />
                        <span className="text-sm text-[#F3EFE7]">Email</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="preferred_contact"
                            value="phone"
                            checked={form.preferred_contact === "phone"}
                            onChange={() => {
                                setField("preferred_contact", "phone");
                                setField("contact_value", "");
                            }}
                            data-testid="review-form-contact-phone"
                            className="accent-[#C9A86A]"
                        />
                        <span className="text-sm text-[#F3EFE7]">Phone</span>
                    </label>
                </div>
            </div>

            <div>
                <label className={labelCls} htmlFor="contact_value">
                    {form.preferred_contact === "email"
                        ? "Email address"
                        : "Phone number"}
                </label>
                <input
                    id="contact_value"
                    type={form.preferred_contact === "email" ? "email" : "tel"}
                    inputMode={form.preferred_contact === "email" ? "email" : "tel"}
                    required
                    autoComplete={form.preferred_contact === "email" ? "email" : "tel"}
                    value={form.contact_value}
                    onChange={(e) => setField("contact_value", e.target.value)}
                    className={inputCls}
                    data-testid="review-form-contact-value"
                    placeholder={
                        form.preferred_contact === "email"
                            ? "you@example.com"
                            : "(555) 123-4567"
                    }
                />
            </div>

            {/* Photo upload */}
            <div>
                <span className={labelCls}>Photos (optional · up to 3)</span>
                <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            fileInputRef.current?.click();
                        }
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`flex flex-col items-center justify-center border border-dashed py-8 px-6 cursor-pointer transition-colors ${
                        dragOver
                            ? "border-[#C9A86A] bg-[#1c1d22]"
                            : "border-[rgba(201,168,106,0.3)] hover:border-[rgba(201,168,106,0.55)] bg-[#0F1013]"
                    }`}
                    data-testid="review-photo-dropzone"
                >
                    <Upload
                        size={22}
                        strokeWidth={1.4}
                        className="text-[#C9A86A] mb-3"
                    />
                    <div className="text-[13px] text-[#F3EFE7]">
                        Drop images here, or click to browse
                    </div>
                    <div className="mt-1 text-[11px] text-[#6e6a62]">
                        JPEG, PNG, WEBP, or HEIC · 5MB max each
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                        multiple
                        className="hidden"
                        onChange={(e) => onPickFiles(e.target.files)}
                        data-testid="review-photo-input"
                    />
                </div>

                {photos.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-3">
                        {photos.map((f, i) => {
                            const url = URL.createObjectURL(f);
                            return (
                                <div
                                    key={f.name + i}
                                    className="relative w-20 h-20 border border-[rgba(201,168,106,0.25)]"
                                    data-testid={`review-photo-thumb-${i}`}
                                >
                                    <img
                                        src={url}
                                        alt={f.name}
                                        className="w-full h-full object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removePhoto(i)}
                                        aria-label={`Remove ${f.name}`}
                                        className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#0B0B0C] border border-[#C9A86A]/60 text-[#F3EFE7] hover:text-[#C9A86A]"
                                        data-testid={`review-photo-remove-${i}`}
                                    >
                                        <X size={12} strokeWidth={1.5} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Honeypot — visible only to bots that fill every input. */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: "-9999px",
                    height: 0,
                    width: 0,
                    overflow: "hidden",
                    opacity: 0,
                }}
            >
                <label htmlFor="website">Website (leave blank)</label>
                <input
                    id="website"
                    type="text"
                    name="website"
                    autoComplete="off"
                    tabIndex={-1}
                    value={form.honeypot}
                    onChange={(e) => setField("honeypot", e.target.value)}
                    data-testid="review-form-honeypot"
                />
            </div>

            {error && (
                <div
                    role="alert"
                    className="border border-[rgba(224,168,154,0.4)] text-[#E0A89A] px-4 py-3 text-[13px]"
                    data-testid="review-form-error"
                >
                    {error}
                </div>
            )}

            <div className="flex items-center justify-end pt-2">
                <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-[#C9A86A] hover:bg-[#D9BC80] text-[#0B0B0C] font-medium text-[11px] tracking-[0.32em] uppercase px-8 py-4 transition-colors disabled:opacity-60 disabled:cursor-wait"
                    data-testid="review-form-submit"
                >
                    {submitting ? "Submitting…" : "Submit review"}
                </button>
            </div>
        </form>
    );
}
